// bulk-bar.ts — Barre d'actions de masse de la liste des actions (#actions),
// fixée en bas du viewport : « n sélectionnées », Fusionner, Dupliquer,
// Affecter au comité, Tout désélectionner. En mode rattachement à une
// réunion (`?meeting=<id>`), elle devient le bandeau « Mettre à jour la
// liste » / « Annuler ».
//
// Première tranche de l'extraction des closures de `renderActionsPage`
// (audit dette technique 2026-09-07, point 16). La barre lisait par capture
// la sélection, le mode d'affichage, les comités, le mode lien et la liste
// des actions, et rappelait trois fonctions de la page. Tout passe
// maintenant par `BulkBarContext` : ce que la barre lit est explicite, ce
// qu'elle modifie sur la page passe par trois rappels.
//
// Les rendus sont des fonctions pures testées ; les appels API (rattachement,
// affectation en masse) restent ici, la page ne fait que réagir au résultat.

import { markLastModifiedMany } from '../../utils/last-modified-anim.js';
import { ensureBulkBarStyles } from './render-page-fragments.js';
import type { ActionsCommittee } from './types.js';

export type BulkBarCommittee = ActionsCommittee;

/** Mode rattachement à une réunion : bandeau dédié toujours visible.
 *
 * Deux phases. Tant que la liste déjà liée n'est pas chargée, « Mettre à
 * jour » n'existe pas : enregistrer une sélection partielle remplacerait la
 * liste réelle de la réunion. Le type l'interdit plutôt que de le rappeler. */
export type BulkBarLink =
  | { meetingId: number; status: 'loading' }
  | {
      meetingId: number;
      status: 'ready';
      label: string;
      /** Actions déjà liées au chargement : sert à n'animer que les ajouts. */
      initialLinkedIds: ReadonlySet<number>;
    };

export interface BulkBarContext {
  /** Mode matrice : pas de sélection, la barre est vidée. */
  hidden: boolean;
  selectedIds: ReadonlySet<number>;
  committees: ReadonlyArray<BulkBarCommittee>;
  link: BulkBarLink | null;
  onClear: () => void;
  onDuplicate: (actionId: number) => void;
  /** Après une affectation en masse réussie : la page met à jour ses actions
   * en mémoire et re-rend la table. */
  onAssigned: (actionIds: number[], committeeId: number) => void;
}

export function linkBarHtml(link: BulkBarLink, n: number): string {
  // Avant chargement : le libellé de la réunion et le compte des liées sont
  // inconnus, et « Mettre à jour » n'est pas proposé.
  const countHtml = `<span style="font-size:12px;color:var(--text2)">${esc(
    link.status === 'ready' ? t('pageActions.link.count', { n }) : t('common.loading'),
  )}</span>`;
  const saveHtml =
    link.status === 'ready'
      ? `<button id="btn-link-save" class="btn btn-primary btn-sm">${esc(
          t('pageActions.link.btnSave'),
        )}</button>`
      : '';
  return (
    `<div class="act-bulk-bar-fixed">` +
    `<span style="font-weight:600">${esc(
      t('pageActions.link.bandeau').replace('{label}', link.status === 'ready' ? link.label : ''),
    )}</span>` +
    countHtml +
    `<span style="flex:1"></span>` +
    `<button id="btn-link-cancel" class="btn btn-secondary btn-sm">${esc(
      t('common.cancel'),
    )}</button>` +
    saveHtml +
    `</div>`
  );
}

/** Trois blocs séparés par un trait vertical : Fusionner + son message
 * d'aide, Dupliquer (à 1 action sélectionnée), combobox comité + Affecter.
 * Gardes de palier : Fusionner → actions.merge ; Dupliquer et Affecter en
 * masse → actions.write. Les endpoints renvoient 403 sinon ; on masque le
 * groupe pour ne pas exposer un CTA inopérant. */
export function bulkBarHtml(
  n: number,
  committees: ReadonlyArray<BulkBarCommittee>,
  perms: { canMerge: boolean; canWrite: boolean },
): string {
  const canMerge = n === 2;
  const canDuplicate = n === 1;
  const hintHtml = canMerge ? '' : esc(t('pageActions.bulk.mergeHint'));
  // Comités triés par position pour le combobox d'affectation en masse.
  const commBulkOptsHtml = committees
    .slice()
    .sort((a, b) => (a.position || 0) - (b.position || 0) || a.code.localeCompare(b.code))
    .map((c) => `<option value="${c.id}">${esc(c.code)} — ${esc(c.name)}</option>`)
    .join('');
  const sepHtml = `<span class="act-bulk-sep" aria-hidden="true"></span>`;
  const mergeGroupHtml = !perms.canMerge
    ? ''
    : `<span class="act-bulk-group">` +
      `<button id="btn-bulk-merge" class="btn btn-primary btn-sm" ${canMerge ? '' : 'disabled'} title="${esc(
        t('pageActions.btn.mergeTitle'),
      )}">${esc(t('pageActions.btn.merge'))}</button>` +
      (hintHtml ? `<span style="font-size:12px;color:var(--text2)">${hintHtml}</span>` : '') +
      `</span>`;
  const dupGroupHtml =
    canDuplicate && perms.canWrite
      ? `<span class="act-bulk-group"><button id="btn-bulk-duplicate" class="btn btn-secondary btn-sm" title="${esc(
          t('pageActions.bulk.duplicateTitle'),
        )}">${esc(t('pageActions.bulk.duplicate'))}</button></span>`
      : '';
  const assignGroupHtml = !perms.canWrite
    ? ''
    : `<span class="act-bulk-group act-bulk-assign">` +
      `<select id="sel-bulk-committee" class="form-control form-control-sm" style="min-width:170px">` +
      `<option value="">${esc(t('pageActions.bulk.assignPlaceholder'))}</option>${commBulkOptsHtml}</select>` +
      `<button id="btn-bulk-assign-committee" class="btn btn-secondary btn-sm">${esc(
        t('pageActions.bulk.assignCommittee'),
      )}</button>` +
      `</span>`;
  const groupsHtml = [mergeGroupHtml, dupGroupHtml, assignGroupHtml].filter(Boolean).join(sepHtml);
  return (
    `<div class="act-bulk-bar-fixed">` +
    `<span style="font-weight:600">${esc(t('pageActions.bulk.selected', { n }))}</span>` +
    groupsHtml +
    `<button id="btn-bulk-clear" class="btn btn-link btn-sm" style="margin-left:auto"` +
    `>${esc(t('pageActions.bulk.clear'))}</button>` +
    `</div>`
  );
}

function wireLinkBar(el: HTMLElement, link: BulkBarLink, selectedIds: ReadonlySet<number>): void {
  const back = (): void => {
    window.location.hash = `#meeting/${link.meetingId}?tab=actions`;
  };
  el.querySelector<HTMLButtonElement>('#btn-link-cancel')?.addEventListener('click', back);
  // Liste liée pas encore chargée : pas de bouton d'enregistrement à câbler.
  if (link.status !== 'ready') return;
  const initialLinkedIds = link.initialLinkedIds;
  el.querySelector<HTMLButtonElement>('#btn-link-save')?.addEventListener('click', () => {
    void (async () => {
      try {
        const finalIds = Array.from(selectedIds);
        // Diff add-only pour l'animation. Le retrait n'est pas animé :
        // une ligne supprimée n'existe simplement plus dans la table
        // côté #meeting/X?tab=actions.
        const addedIds = finalIds.filter((id) => !initialLinkedIds.has(id));
        await api.setMeetingActions(link.meetingId, finalIds);
        if (addedIds.length) {
          markLastModifiedMany('meeting-actions', addedIds);
        }
        toast(t('common.saved'), 'ok');
        back();
      } catch (err: unknown) {
        toast(err instanceof Error ? err.message : t('common.error'), 'error');
      }
    })();
  });
}

function wireBulkBar(el: HTMLElement, ctx: BulkBarContext): void {
  const { selectedIds, committees } = ctx;
  el.querySelector<HTMLButtonElement>('#btn-bulk-clear')?.addEventListener('click', () =>
    ctx.onClear(),
  );
  el.querySelector<HTMLButtonElement>('#btn-bulk-merge')?.addEventListener('click', () => {
    if (selectedIds.size !== 2) return;
    const [a, b] = [...selectedIds];
    // Page dédiée plutôt que modale (cohérence refonte UX mai 2026 —
    // « popups vers pages dédiées »). Convention de l'URL : keeper en
    // premier ; la page laisse l'utilisateur inverser via bouton.
    router.navigate(`#actions/merge/${a}/${b}`);
  });
  el.querySelector<HTMLButtonElement>('#btn-bulk-duplicate')?.addEventListener('click', () => {
    if (selectedIds.size !== 1) return;
    const [only] = [...selectedIds];
    if (only !== undefined) ctx.onDuplicate(only);
  });
  el.querySelector<HTMLButtonElement>('#btn-bulk-assign-committee')?.addEventListener(
    'click',
    () => {
      const sel = el.querySelector<HTMLSelectElement>('#sel-bulk-committee');
      const cid = parseInt(sel?.value || '0');
      if (!cid) {
        toast(t('pageActions.bulk.assignNoCommittee'), 'error');
        return;
      }
      const comm = committees.find((c) => c.id === cid);
      const label = comm ? `${comm.code} — ${comm.name}` : String(cid);
      if (!confirm(t('pageActions.bulk.assignConfirm', { committee: label }))) return;
      const ids = [...selectedIds];
      void (async () => {
        try {
          const res = (await api.bulkAssignCommittee({
            action_ids: ids,
            committee_id: cid,
          })) as { updated: number };
          toast(t('pageActions.bulk.assignDone', { n: res.updated, committee: label }), 'ok');
          ctx.onAssigned(ids, cid);
        } catch (err: unknown) {
          toast(err instanceof Error ? err.message : t('common.error'), 'error');
        }
      })();
    },
  );
}

/** (Re)rend la barre dans `el` et câble ses CTA. Sticky en bas du viewport
 * (cf. user 2026-05-21) : `el` n'est qu'un hôte vide, on injecte un enfant
 * position:fixed. */
export function renderBulkBarInto(el: HTMLElement, ctx: BulkBarContext): void {
  if (ctx.hidden) {
    el.innerHTML = '';
    return;
  }
  // Mode rattachement : bandeau dédié toujours visible (même si la
  // sélection est vide — l'utilisateur doit pouvoir valider « 0 action
  // principale » pour vider la liste). Pas de Fusionner ni de Tout
  // désélectionner : Annuler suffit pour sortir.
  if (ctx.link) {
    ensureBulkBarStyles();
    el.innerHTML = linkBarHtml(ctx.link, ctx.selectedIds.size);
    wireLinkBar(el, ctx.link, ctx.selectedIds);
    return;
  }
  const n = ctx.selectedIds.size;
  if (n === 0) {
    el.innerHTML = '';
    return;
  }
  const canMerge = !!(window.hasPermission && window.hasPermission('actions.merge'));
  const canWrite = !!(window.hasPermission && window.hasPermission('actions.write'));
  el.innerHTML = bulkBarHtml(n, ctx.committees, { canMerge, canWrite });
  ensureBulkBarStyles();
  wireBulkBar(el, ctx);
}
